// Cihaz Yönetimi sayfası kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'devices.management';

export const devicesManagementGuide = (t: TFunction): GuideContent => ({
  layout: 'list',
  title: t(`${G}.title`, 'Cihaz Yönetimi nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'TV ekranlarınızı (medya, sipariş), kiosk, geçiş kontrol ve PDKS bilgisayarlarını, PavoPay cihazlarını ve satış ekranı kodlarını buradan tanımlarsınız. Her cihaz için bir kod üretilir; bu kodu cihazdaki uygulamaya girince cihaz bağlanır. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Cihaz Yönetimi'),
    filters: t(`${G}.regions.filters`, 'Şube Filtresi'),
    toolbar: t(`${G}.regions.toolbar`, 'Yenile / Cihaz Ekle'),
    table: t(`${G}.regions.table`, 'Cihaz listesi'),
  },
  steps: [
    {
      region: 'toolbar',
      title: t(`${G}.s1.title`, '1. Cihaz türünü seçin'),
      text: t(`${G}.s1.text`, '"Cihaz Ekle" düğmesi bir liste açar: Medya Ekranı, Sipariş Ekranı, Kiosk Ekranı, Geçiş Kontrol Bilgisayarı, PDKS, PavoPay Cihazı ve Satış Ekranı Ekle. Kuracağınız cihaza uyan türü seçin; her tür kendi penceresini açar.'),
      tips: [
        t(`${G}.s1.tip1`, 'Medya Ekranı: TV\'de resim/video gösterir. Sipariş Ekranı: Android TV\'de QR menü siparişlerini takip ekranı olarak gösterir.'),
        t(`${G}.s1.tip2`, 'Sipariş Ekranı için önce bir QR menü, Kiosk Ekranı için önce Kiosk Menü Yönetimi sayfasından bir kiosk tanımlanmış olmalıdır; yoksa bu seçenekler soluk görünür.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s2.title`, '2. Ekranı veya cihazı ekleyin'),
      text: t(`${G}.s2.text`, 'Pencerede Ekran Adı / Cihaz Adı zorunludur; Açıklama isteğe bağlıdır. Medya ve sipariş ekranlarında "Ekran Yönü" (Yatay, Dikey Sol, Dikey Sağ) seçilir. "Ekle"ye basınca kod üretilir ve cihaz listeye "Kurulum Bekliyor" durumunda eklenir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Medya, Geçiş Kontrol, PDKS ve PavoPay pencerelerinde "Şube (opsiyonel)" alanı vardır; boş bırakılırsa cihaz doğrudan şirkete bağlanır.'),
        t(`${G}.s2.tip2`, 'Sipariş ekranının şubesi seçtiğiniz QR menüden, kiosk ekranının şubesi seçtiğiniz kiosktan alınır.'),
        t(`${G}.s2.tip3`, 'Cihaz sayfadaki şube filtresinden farklı bir şubeye kaydedilirse listede görünmez; görmek için filtreyi değiştirin.'),
      ],
    },
    {
      region: 'toolbar',
      title: t(`${G}.s3.title`, '3. PavoPay cihazı ve satış ekranı kodu'),
      text: t(`${G}.s3.text`, 'PavoPay Cihazı penceresinde Cihaz Modu seçilir: Sipariş Modu, Hızlı Satış Modu, Restoran Modu veya Rest-P2P Modu. Hızlı Satış ve Restoran modunda cihazın ayarlarını kullanacağı ekranı da seçmeniz zorunludur. "Satış Ekranı Ekle" ile Hedef Mod (Hızlı Satış, Restoran, Paket Sipariş, PYK) ve Bağlanacak Ekran seçilip "Kod Üret"e basılır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Satış ekranı kodu local-service\'teki "Satış Ekranı Bağla" sayfasına girildiğinde, seçilen ekran personel girişi olmadan açılır.'),
        t(`${G}.s3.tip2`, 'Satış ekranı kodunda şube seçilmez; cihazın şubesi bağladığınız ekrandan alınır.'),
        t(`${G}.s3.tip3`, 'Bağlanacak ekran listesi sayfadaki şube filtresine göre süzülür; yalnız aktif ekranlar çıkar.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s4.title`, '4. Kodu cihaza girin'),
      text: t(`${G}.s4.text`, 'Üretilen kod "Ekran Kodu" sütununda yazar; yanındaki kopyala düğmesiyle alabilirsiniz. Kodu girdiğiniz yer cihaz türüne göre değişir:'),
      tips: [
        t(`${G}.s4.tip1`, 'Medya ekranı ve PavoPay: Flutter uygulamasında kurulum / giriş sırasında girilir. Sipariş ekranı: kod Android TV\'de sipariş takip ekranını açar.'),
        t(`${G}.s4.tip2`, 'Kiosk: 11 haneli kodla kiosk uygulamasında giriş yapılır.'),
        t(`${G}.s4.tip3`, 'Geçiş Kontrol Bilgisayarı: 11 haneli kod fabrikadaki panel bilgisayarında, local-service\'te Seçenekler > Geçiş Yönetimi sayfasına girilir. Kod doğrulanmadan hiçbir kapı sunucuya bildirilemez.'),
        t(`${G}.s4.tip4`, 'PDKS: 11 haneli kod local-service\'e (POST /api/pdks/pair) girilir. Kod doğrulanmadan terminal bildirilemez.'),
        t(`${G}.s4.tip5`, 'Kod sonradan değiştirilemez.'),
      ],
    },
    {
      region: 'table',
      title: t(`${G}.s5.title`, '5. Bağlantı durumunu izleyin'),
      text: t(`${G}.s5.text`, 'Durum sütunu: "Kurulum Bekliyor" kod henüz cihaza girilmedi demektir; kod cihazda doğrulanınca "Aktif" olur. "Cihaz Bilgisi" bağlanan cihazın model ve sürümünü, "Son Görülme" cihazın en son ne zaman bağlandığını gösterir. Bağlanmamış cihazda "Henüz bağlanmadı" yazar.'),
      tips: [
        t(`${G}.s5.tip1`, 'Listeyi tazelemek için Yenile düğmesine basın.'),
      ],
    },
    {
      region: 'filters',
      title: t(`${G}.s6.title`, '6. Şubeye göre süzün'),
      text: t(`${G}.s6.text`, '"Şube Filtresi" boşken şirketin tüm cihazları (şubesizler dahil) listelenir. Bir şube seçince yalnız o şubenin cihazları görünür. Filtre yalnız listeyi süzer; yeni cihazın şubesini pencere içindeki alan belirler.'),
    },
    {
      region: 'table',
      title: t(`${G}.s7.title`, '7. Düzenleyin, medya yükleyin veya silin'),
      text: t(`${G}.s7.text`, 'Satırdaki ⋮ menüsü: "Düzenle" yalnız medya ve sipariş ekranlarında vardır (ad, açıklama, yön; medyada şube, siparişte QR menü). Medya ekranında "Medya Yönetimi" ekrana gösterilecek içerikleri açar; sipariş ekranında "Ekranı Aç" ekranı yeni sekmede açar.'),
      tips: [
        t(`${G}.s7.tip1`, '"Sil" cihaz kaydını kalıcı olarak siler ve geri alınamaz; ekrana yüklenmiş medya ayarları da silinir. Kod artık çalışmaz, cihazı yeniden kurmak için yeni kod üretmeniz gerekir.'),
        t(`${G}.s7.tip2`, 'Satış ekranı kodu silinince o kodla açılmış oturumların erişimi de kapatılır.'),
      ],
    },
  ],
});
